// angular
import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
// components
import { AuctionCardComponent } from '@app/core/components/auction-card/auction-card.component';
import { PaginationComponent } from '@app/core/components/pagination/pagination.component';
import { NoContentComponent } from '@app/core/components/no-content/no-content.component';
import { AuctionCardSkeletonComponent } from '@app/core/components/auction-card/auction-card-skeleton/auction-card-skeleton.component';
import { SortsBarComponent } from '@app/core/components/sorts-bar/sorts-bar.component';
import { FiltersPanelComponent } from '@app/core/components/filters-panel/filters-panel.component';
import { ProductsFiltersComponent } from '@app/+products/products-list/components/products-filters/products-filters.component';
import { ProductsFiltersDialogComponent } from '@app/+products/products-list/dialogs/products-filters-dialog/products-filters-dialog.component';
// services
import { AuctionsService } from '../shared/services/auctions.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
// types & enums
import { IAuction } from '../shared/types/auctions.type';
import { AuctionStatesEnum } from '../shared/enums/auction-states.enum';

@Component({
  selector: 'pwj-auctions-list',
  standalone: true,
  imports: [
    SortsBarComponent,
    AuctionCardComponent,
    PaginationComponent,
    TranslateModule,
    NgxSkeletonLoaderModule,
    NoContentComponent,
    AuctionCardSkeletonComponent,
    FiltersPanelComponent,
    ProductsFiltersComponent,
  ],
  templateUrl: './auctions-list.component.html',
  styleUrl: './auctions-list.component.scss',
})
export class AuctionsListComponent implements OnInit {
  public filters: string[] = Object.values(AuctionStatesEnum);
  public currentTab?: string;
  public auctionsData: IAuction[] = [];
  public allAuctionsCount!: number;
  public isLoading = true;

  readonly filtersSheetOpen = signal(false);
  readonly showFiltersTriggerOnSortBar = signal(true);

  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _auctionsService = inject(AuctionsService);
  private readonly _dialogService = inject(DialogService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {
    effect(() => {
      this.allAuctionsCount = this._auctionsService.allAuctionsCount();
    });

    effect(() => {
      this.isLoading = this._auctionsService.isAuctionsLoading();
    });

    effect(() => {
      this.auctionsData = this._auctionsService.auctionsList();
    });

    afterNextRender(() => {
      if (!isPlatformBrowser(this._platformId)) {
        return;
      }
      const mq = window.matchMedia('(max-width: 65.99em)');
      const sync = () => this.showFiltersTriggerOnSortBar.set(mq.matches);
      sync();
      mq.addEventListener('change', sync);
      this._destroyRef.onDestroy(() => mq.removeEventListener('change', sync));
    });
  }

  ngOnInit(): void {
    this._activatedRoute.queryParams.subscribe(params => {
      this.currentTab = params['filter_status'];
      const take = params['perPage'] || 6;
      const skip = params['page'] ? (Number(params['page']) - 1) * take : 0;

      let selectedParams: Params = {
        ...params,
        take,
        skip,
        filter_status: this.currentTab ? [this.currentTab] : undefined,
        page: null,
        perPage: null,
      };

      selectedParams = Object.fromEntries(
        Object.entries(selectedParams).filter(
          ([_, value]) => value != null && value !== ''
        )
      );

      this._auctionsService.getAllAuctions(selectedParams);
    });
  }

  onFiltersFromSortBar(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    if (window.matchMedia('(min-width: 48em)').matches) {
      this.openFiltersDialog();
    } else {
      this.openFiltersSheet();
    }
  }

  openFiltersSheet(): void {
    this.filtersSheetOpen.set(true);
  }

  closeFiltersSheet(): void {
    this.filtersSheetOpen.set(false);
  }

  openFiltersDialog(): void {
    this._dialogService.open(ProductsFiltersDialogComponent, {
      width: 'min(23.5rem, calc(100vw - 1.25rem))',
      maxWidth: 'calc(100vw - 1.25rem)',
      maxHeight: 'min(90vh, 44rem)',
      height: 'auto',
      panelClass: 'overlay-panel--filters-dialog',
    });
  }
}
